import * as path from 'path';

import { makeNamespacedLog } from '@sb/log';
import { readJSONFileWithFallback } from '@sb/service-interfaces/host/util';

const log = makeNamespacedLog('localCaptureSettings');

const SETTINGS_PATH = '/etc/standardbots/configuration/capture_retention.json';

/**
 * Local collection is INDEPENDENT of `ACTIVE_LEARNING_ENABLED`, and must stay
 * so.
 *
 * That flag is a build-time `ENV` on `roboflow-inference-bot` governing whether
 * the inference container uploads sampled frames back to Roboflow. It is a
 * different destination, a different credential and a different failure mode
 * from writing a frame to this robot's own disk, and the two are deliberately
 * not one switch: a customer running fully offline still wants their own
 * records, and turning Roboflow collection off must not silently stop the local
 * ones.
 *
 * Nothing here reads that flag today. Anyone wiring it up should keep it scoped
 * to the Roboflow upload path, and leave a `kind: 'local'` target answering only
 * to its own configuration.
 */

/**
 * The tree bind mounted into botman, as
 * `-v /etc/standardbots:/etc/standardbots` in `golang/libs/services/botman`.
 * Everything captures touch lives inside it.
 */
export const CAPTURE_MOUNT_ROOT = '/etc/standardbots';

/** What a local capture target writes to, and how much of it is kept. */
export interface LocalCaptureSettings {
  /** The directory every local capture writes into. */
  directory: string;
  /** Total bytes the directory may hold. */
  maxTotalBytes: number;
  /**
   * Free bytes the filesystem must keep, whatever the directories hold.
   *
   * A second bound because `maxTotalBytes` is enforced per leaf, and captures
   * now live one directory per skill. Ten skills inside their own budgets can
   * still fill a disk between them, and nothing else on the robot survives
   * that: the ENOSPC goes to whichever service writes next.
   */
  minFreeBytes: number;
}

/**
 * The bounds used when the robot has no retention config.
 *
 * `/etc/standardbots` is where per-robot data already lives — `blobStore`
 * ("uploaded images, CAD and model files") is the closest precedent — and it is
 * the tree botman is launched with mounted (`-v
 * /etc/standardbots:/etc/standardbots`). That mount is the load-bearing part:
 * captures are written from inside the botman container and pruned by `botctl`
 * on the host, so a directory the container does not share with the host gives
 * those two halves different filesystems. botman also runs `--rm`, so an
 * unmounted path would lose every frame on the next service restart. It sits
 * beside `/etc/standardbots/configuration` and outside it, because that
 * directory is collected into support bundles and a few hundred megabytes of
 * defect frames have no place in one.
 *
 * Size is the only bound. Age and a file count were both bounds here once, and
 * both decided retention on something other than what the operator is actually
 * out of: a cell inspecting all day hit them while well inside its footprint,
 * and a quiet one kept frames it had room for. What a robot runs out of is
 * disk, so that is what rotation answers to.
 *
 * THIS IS THE VALUE A BROKEN CONFIG FALLS BACK TO, and that is why it is a
 * bound. `readJSONFileWithFallback` catches every read error and returns the
 * fallback, so a malformed or unreadable file is indistinguishable from an
 * absent one — a fallback of "keep everything" would turn one typo in a JSON
 * file into a directory that fills the robot's disk, with nothing logged and
 * nothing failing until something unrelated hits ENOSPC.
 */
export const DEFAULT_LOCAL_CAPTURE_SETTINGS: LocalCaptureSettings = {
  directory: '/etc/standardbots/captures',
  maxTotalBytes: 4 * 1024 * 1024 * 1024,
  minFreeBytes: 2 * 1024 * 1024 * 1024,
};

/**
 * Whether `directory` resolves to the captures directory or a path below it.
 *
 * Two separate hazards, which is why the bound is the captures directory and
 * not the mount.
 *
 * Outside the mount, nothing about choosing a path fails. `writeCaptureLocally`
 * creates it with `mkdir -p`, so the write succeeds INSIDE botman's container;
 * botman runs `--rm`, so the next service restart destroys it; and `botctl
 * captures status` runs on the host, where it reads a different, empty
 * directory and reports that the robot is holding nothing. Every layer reports
 * success while the frames are thrown away.
 *
 * Inside the mount but somewhere captures do not own, the failure is worse,
 * because pruning deletes. `/etc/standardbots/blobStore` holds feathers-blob's
 * objects, named `<hash>.<ext>` and referenced by rows in its database, so a
 * retention file naming it would have the prune that runs after every capture
 * delete those blobs to stay inside the size bound while feathers still
 * believed they existed. `backups/` and `configuration/` are the same shape of mistake.
 *
 * Compared with `path.relative` instead of a string prefix, so a sibling like
 * `/etc/standardbots/captures-old` is rejected: it shares the prefix without
 * being inside the tree. Organising within the tree (`captures/line-4`) works.
 */
export function isWithinCaptureMount(directory: string): boolean {
  if (!path.isAbsolute(directory)) return false;

  const relative = path.relative(
    DEFAULT_LOCAL_CAPTURE_SETTINGS.directory,
    path.resolve(directory),
  );

  // An empty relative path IS accepted: it means the captures directory itself,
  // which is the ordinary answer and has to pass its own guard.
  if (relative === '..' || relative.startsWith(`..${path.sep}`)) return false;

  return !path.isAbsolute(relative);
}

/**
 * The directory to write into, falling back when the configured one is outside
 * the mount.
 *
 * This falls back and logs. It resolves on the capture path, where a throw
 * fails the routine step, and a misconfigured retention file should not stop a
 * robot inspecting parts. The frames go somewhere durable and the reason is
 * recorded.
 */
function resolveDirectory(configured: string | undefined): string {
  if (configured == null || configured === '') {
    return DEFAULT_LOCAL_CAPTURE_SETTINGS.directory;
  }

  if (!isWithinCaptureMount(configured)) {
    log.warn(
      'localCaptureSettings',
      'Ignoring a capture directory outside the mounted tree',
      {
        configured,
        mountRoot: CAPTURE_MOUNT_ROOT,
        using: DEFAULT_LOCAL_CAPTURE_SETTINGS.directory,
        settingsPath: SETTINGS_PATH,
      },
    );

    return DEFAULT_LOCAL_CAPTURE_SETTINGS.directory;
  }

  return path.resolve(configured);
}

/**
 * Where a step's captures go when it names no skill.
 *
 * A plain `Capture` step has none, and a Locate step can run before one is
 * chosen. Those frames still need a bounded home, so they get a directory
 * beside the per-skill ones — `captures/locate/unattributed/` — with its own
 * budget, rather than falling back to a level the prune for a skill would walk.
 */
export const UNATTRIBUTED_CAPTURE_DIRECTORY = 'unattributed';

/**
 * Characters a skill id may contribute to a path component.
 *
 * Skill ids are server-issued and alphanumeric, so this rejects nothing real.
 * It is here because the value is joined onto a directory the prune DELETES in,
 * and `path.join(root, '..', '..')` escapes it without complaint. An id that
 * fails this is treated as no id at all rather than sanitized into a different
 * one: two ids that scrubbed to the same component would share a budget and
 * rotate each other's frames away.
 */
const SAFE_PATH_ID = /^[A-Za-z0-9_-]+$/;

/**
 * The directory one skill's captures are written to and bounded in.
 *
 * `<root>/<inspect|locate|capture>/<skillId>/`, so the budget is per skill. One
 * directory for all of them made every skill's retention depend on how busy its
 * neighbours were: a cell inspecting all day on one skill would rotate away
 * another skill's records before that skill had written enough to be over
 * anything.
 *
 * The kind comes first so a directory reads as what it holds before it reads as
 * which one, and so `captures/inspect` is one place to look for every
 * inspection this robot has recorded.
 *
 * An ID rather than a name at the second level, because a skill renamed in the
 * wizard would otherwise change its directory — stranding every frame it had
 * already written in a directory nothing counts, prunes or reports on again.
 * The routine is in the FILENAME instead, so a skill used by several routines
 * keeps one budget and one place to look.
 */
export function captureDirectoryFor(args: {
  /** The configured captures root. */
  root: string;
  /** `inspect`, `locate` or `capture`; see `captureKindDirectory`. */
  kind: string;
  skillId?: string;
}): string {
  const { root, kind, skillId } = args;

  const attributed = skillId != null && SAFE_PATH_ID.test(skillId);

  return path.join(
    root,
    kind,
    attributed ? skillId : UNATTRIBUTED_CAPTURE_DIRECTORY,
  );
}

/**
 * Reads the robot's local-capture settings, falling back to the bounds above.
 *
 * This lives in robot configuration, which is what keeps a step and `botctl`
 * from disagreeing about the cap: the pruning that runs after a capture and the
 * pruning an operator invokes by hand read the same file.
 *
 * Each field falls back independently, so a config that sets only `directory`
 * still gets bounded retention.
 *
 * The directory is additionally checked against `CAPTURE_MOUNT_ROOT`, because
 * that is the one field whose value can be accepted, written to, and still lose
 * every frame.
 */
export async function loadLocalCaptureSettings(): Promise<LocalCaptureSettings> {
  const stored = await readJSONFileWithFallback<Partial<LocalCaptureSettings>>(
    SETTINGS_PATH,
    {},
  );

  return {
    directory: resolveDirectory(stored.directory),
    maxTotalBytes:
      stored.maxTotalBytes ?? DEFAULT_LOCAL_CAPTURE_SETTINGS.maxTotalBytes,
    minFreeBytes:
      stored.minFreeBytes ?? DEFAULT_LOCAL_CAPTURE_SETTINGS.minFreeBytes,
  };
}
